import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('gmt_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('gmt_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('gmt_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const registerParent = async (data) => {
    const response = await axios.post(`${API}/auth/register/parent`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('gmt_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const registerStudent = async (data) => {
    const response = await axios.post(`${API}/auth/register/student`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('gmt_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const api = {
    get: (url) => axios.get(`${API}${url}`, { headers: getAuthHeaders() }),
    post: (url, data) => axios.post(`${API}${url}`, data, { headers: getAuthHeaders() }),
    put: (url, data) => axios.put(`${API}${url}`, data, { headers: getAuthHeaders() }),
    delete: (url) => axios.delete(`${API}${url}`, { headers: getAuthHeaders() }),
  };

  const value = {
    user,
    token,
    loading,
    login,
    registerParent,
    registerStudent,
    logout,
    updateUser,
    getAuthHeaders,
    api,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
